<template>
  <div class="uic-card">

    <!-- Hero Header -->
    <div class="uic-hero">
      <!-- Avatar + Name row -->
      <div class="uic-hero-main">
        <div class="uic-avatar-wrap">
          <div class="uic-avatar" :style="{ background: getAvatarColor(userData.fullName) }">
            <img v-if="userData.avatar" :src="userData.avatar" class="uic-avatar-img" :alt="userData.fullName" />
            <span v-else class="uic-avatar-initials">{{ getInitials(userData.fullName) }}</span>
          </div>
          <span class="uic-status-dot" :class="userData.status === 'Ativo' ? 'uic-dot-active' : 'uic-dot-inactive'" :title="userData.status"></span>
        </div>

        <div class="uic-hero-info">
          <h2 class="uic-name">{{ userData.fullName }}</h2>
          <p class="uic-email">{{ userData.email }}</p>
          <div class="uic-badges">
            <span class="uic-badge" :class="userData.role === 'admin' ? 'uic-badge-admin' : 'uic-badge-client'">
              {{ userData.role === 'admin' ? '★ Admin' : '● Cliente' }}
            </span>
            <span class="uic-badge" :class="userData.status === 'Ativo' ? 'uic-badge-ativo' : 'uic-badge-inativo'">
              {{ userData.status === 'Ativo' ? '✓ Ativo' : '✕ Inativo' }}
            </span>
          </div>
        </div>

        <button class="uic-btn-edit" @click="$emit('edit', userData)">
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2"/></svg>
          Editar
        </button>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="uic-stats">
      <div class="uic-stat">
        <div class="uic-stat-icon uic-stat-icon-blue">
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><polygon points="23 7 16 12 23 17 23 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" stroke-width="2"/></svg>
        </div>
        <div>
          <div class="uic-stat-num">{{ userData.qtde_vsl || 0 }}</div>
          <div class="uic-stat-lbl">VSLs criados</div>
        </div>
      </div>
      <div class="uic-stat-divider"></div>
      <div class="uic-stat">
        <div class="uic-stat-icon uic-stat-icon-green">
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
        </div>
        <div>
          <div class="uic-stat-num">{{ (userData.qtde_load || 0).toLocaleString('pt-BR') }}</div>
          <div class="uic-stat-lbl">Visualizações</div>
        </div>
      </div>
      <div class="uic-stat-divider"></div>
      <div class="uic-stat">
        <div class="uic-stat-icon uic-stat-icon-orange">
          <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
        <div>
          <div class="uic-stat-num">{{ userData.qtde_play || 0 }}</div>
          <div class="uic-stat-lbl">Plays consumidos</div>
        </div>
      </div>
    </div>

    <!-- Details Grid -->
    <div class="uic-details">
      <h4 class="uic-section-title">Informações do usuário</h4>
      <div class="uic-details-grid">

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">Nome de usuário</div>
            <div class="uic-detail-value">@{{ userData.username || '—' }}</div>
          </div>
        </div>

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="2"/><polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">E-mail</div>
            <div class="uic-detail-value">{{ userData.email || '—' }}</div>
          </div>
        </div>

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.8 8.62 19.79 19.79 0 01.7 2.05 2 2 0 012.68 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.64a16 16 0 006.29 6.29l.78-.78a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">Contato</div>
            <div class="uic-detail-value">{{ userData.contact || '—' }}</div>
          </div>
        </div>

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">Cargo / Função</div>
            <div class="uic-detail-value">{{ userData.role === 'admin' ? 'Administrador' : 'Cliente' }}</div>
          </div>
        </div>

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2"/><line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2"/><line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2"/><line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">Data de cadastro</div>
            <div class="uic-detail-value">{{ formatDate(userData.created_at) }}</div>
          </div>
        </div>

        <div class="uic-detail-item">
          <div class="uic-detail-icon">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" stroke-width="2"/><polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" stroke-width="2"/></svg>
          </div>
          <div>
            <div class="uic-detail-label">Status</div>
            <div class="uic-detail-value">
              <span class="uic-status-pill" :class="userData.status === 'Ativo' ? 'uic-pill-ativo' : 'uic-pill-inativo'">
                <span class="uic-pill-dot"></span>{{ userData.status }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'UserViewUserInfoCard',
  props: {
    userData: { type: Object, required: true },
  },
  methods: {
    getInitials(name) {
      if (!name || !name.trim()) return '?'
      const parts = name.trim().split(' ')
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase()
    },
    getAvatarColor(name) {
      const colors = ['#E86A10', '#0D3349', '#3A86FF', '#8338EC', '#06D6A0', '#EF476F', '#118AB2']
      if (!name) return colors[0]
      return colors[name.charCodeAt(0) % colors.length]
    },
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric',
        timeZone: 'America/Sao_Paulo',
      })
    },
  },
}
</script>

<style scoped>
/* ─── Card ─── */
.uic-card {
  background: var(--dk-bg-card, #fff); border-radius: 16px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}

/* ─── Hero ─── */
.uic-hero {
  padding: 24px 24px 20px;
  border-bottom: 1.5px solid var(--dk-border-light, #f5f6fa);
  background: linear-gradient(135deg, #f8faff 0%, #fff 100%);
}
.uic-hero-main {
  display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
}

/* ─── Avatar ─── */
.uic-avatar-wrap { position: relative; flex-shrink: 0; }
.uic-avatar {
  width: 76px; height: 76px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  overflow: hidden;
}
.uic-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.uic-avatar-initials { font-size: 26px; font-weight: 700; color: #fff; line-height: 1; }
.uic-status-dot {
  position: absolute; bottom: 2px; right: 2px;
  width: 14px; height: 14px; border-radius: 50%;
  border: 2.5px solid var(--dk-bg-card, #fff);
}
.uic-dot-active { background: #28c76f; }
.uic-dot-inactive { background: #82868b; }

/* ─── Hero Info ─── */
.uic-hero-info { flex: 1; min-width: 0; }
.uic-name {
  font-size: 20px; font-weight: 700; color: var(--dk-text-primary, #0D3349);
  margin: 0 0 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.uic-email { font-size: 13.5px; color: var(--dk-text-muted, #8a94a6); margin: 0 0 10px; }
.uic-badges { display: flex; gap: 7px; flex-wrap: wrap; }
.uic-badge {
  display: inline-block;
  padding: 3px 12px; border-radius: 20px;
  font-size: 12.5px; font-weight: 600; line-height: 1.6;
  white-space: nowrap;
}
.uic-badge-admin { background: rgba(232,106,16,0.12); color: #c45e0a; border: 1px solid rgba(232,106,16,0.25); }
.uic-badge-client { background: rgba(0,168,186,0.1); color: #00869a; border: 1px solid rgba(0,168,186,0.25); }
.uic-badge-ativo { background: rgba(40,199,111,0.1); color: #1e9957; border: 1px solid rgba(40,199,111,0.25); }
.uic-badge-inativo { background: rgba(130,134,139,0.1); color: var(--dk-text-secondary, #6b7280); border: 1px solid rgba(130,134,139,0.2); }

/* ─── Edit Button ─── */
.uic-btn-edit {
  display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0;
  padding: 9px 18px; border-radius: 10px;
  background: var(--dk-bg-card, #fff); color: #E86A10;
  border: 1.5px solid rgba(232,106,16,0.35);
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.uic-btn-edit:hover { background: #E86A10; color: #fff; border-color: #E86A10; }

/* ─── Stats Row ─── */
.uic-stats {
  display: flex; align-items: center;
  padding: 20px 28px; border-top: 1.5px solid var(--dk-border-light, #f5f6fa);
  border-bottom: 1.5px solid var(--dk-border-light, #f5f6fa);
  gap: 0;
}
.uic-stat {
  flex: 1; display: flex; align-items: center; gap: 14px;
  padding: 0 20px;
}
.uic-stat:first-child { padding-left: 0; }
.uic-stat:last-child { padding-right: 0; }
.uic-stat-divider {
  width: 1px; height: 40px; background: var(--dk-bg-inset, #f0f2f5); flex-shrink: 0;
}
.uic-stat-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.uic-stat-icon-blue { background: rgba(58,134,255,0.1); color: #3A86FF; }
.uic-stat-icon-green { background: rgba(40,199,111,0.1); color: #28c76f; }
.uic-stat-icon-orange { background: rgba(232,106,16,0.1); color: #E86A10; }
.uic-stat-num { font-size: 22px; font-weight: 700; color: var(--dk-text-primary, #0D3349); line-height: 1.1; }
.uic-stat-lbl { font-size: 12px; color: var(--dk-text-muted, #8a94a6); margin-top: 2px; font-weight: 500; }

/* ─── Details ─── */
.uic-details { padding: 24px 28px 28px; }
.uic-section-title {
  font-size: 13px; font-weight: 700; color: var(--dk-text-muted, #8a94a6);
  text-transform: uppercase; letter-spacing: 0.08em;
  margin: 0 0 18px;
}
.uic-details-grid {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 18px 32px;
}
.uic-detail-item { display: flex; align-items: flex-start; gap: 12px; }
.uic-detail-icon {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  background: var(--dk-bg-inset, #f5f6fa); color: var(--dk-text-muted, #8a94a6);
  display: flex; align-items: center; justify-content: center;
  margin-top: 1px;
}
.uic-detail-label { font-size: 11.5px; color: var(--dk-text-faint, #aab0bc); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px; }
.uic-detail-value { font-size: 14px; color: var(--dk-text-primary, #344563); font-weight: 500; }

.uic-status-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px; border-radius: 20px; font-size: 12.5px; font-weight: 600;
}
.uic-pill-ativo { background: rgba(40,199,111,0.1); color: #28c76f; }
.uic-pill-inativo { background: rgba(130,134,139,0.12); color: #82868b; }
.uic-pill-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

@media (max-width: 640px) {
  .uic-details-grid { grid-template-columns: 1fr; }
  .uic-stats { flex-direction: column; gap: 16px; }
  .uic-stat-divider { width: 100%; height: 1px; }
  .uic-stat { padding: 0; }
}
</style>
